/**
 * Iter-116 PART 5 — Backup Safety Mode.
 *
 * NOT a hard block. When a project is opened (and once per session
 * per project), if the project has no active backup connection AND
 * the current user is owner/admin, surface a soft modal explaining
 * that backups protect the production memory. Two buttons:
 *
 *   • Connect Dropbox — jump to /backups page
 *   • Continue without backup — dismiss; we record the dismissal in
 *     localStorage so we don't pester the user every time they open
 *     the project today.
 *
 * Members (non-admin) never see this — they can't manage backup
 * settings anyway.
 */

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Cloud, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";


function dismissalKey(projectId) {
    // Per-day dismissal. Reset each calendar day so the user is
    // softly reminded — never blocked.
    const today = new Date().toISOString().slice(0, 10);
    return `onsetflow:backup-prompt-dismissed:${projectId}:${today}`;
}


export default function BackupSafetyModal({
    projectId,
    canManage,
    enabled,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const routeBase = useRouteBase();
    const apiBase = useApiBase();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!enabled || !projectId || !canManage) return;
        // Iter-128 + Iter-129 — Suppress the nag everywhere EXCEPT
        // the project Overview (`/projects/:id` exact). Pilot
        // requirement: working surfaces (/prep, /onset, /scenes,
        // /cast, /settings, /flags, /script, etc.) must stay calm
        // and never have an auto-mounted dialog intercepting pointer
        // events on first paint. The reminder still fires on the
        // Overview — that's where the user actually lands on a fresh
        // project visit and where a nudge is welcome.
        const path = (location.pathname || "").replace(/\/+$/, "");
        const overviewMatch = path.match(/^\/projects\/[^/]+$/);
        if (!overviewMatch) return;
        let cancelled = false;
        (async () => {
            try {
                if (window.localStorage.getItem(dismissalKey(projectId))) return;
                // Iter-156A.5 Phase 5 — never show the backup nag on
                // archived projects. The archive banner is already
                // visible on the overview; stacking a second modal on
                // top of it both contradicts the calm read-only
                // promise AND obscures the Unarchive CTA. Backup
                // connection changes can't be made on an archived
                // project anyway (the write-lock middleware refuses).
                const { data: perms } = await api.get(
                    `${apiBase}/${projectId}/permissions/me`
                );
                if (cancelled) return;
                if (perms && perms.archived) return;
                const { data } = await api.get(
                    `${apiBase}/${projectId}/backup/connection`
                );
                if (cancelled) return;
                if (!data.connection || !data.connection.enabled) {
                    setOpen(true);
                }
            } catch {
                // Silent — connection probe failures shouldn't block
                // the user from opening the project. If perms are
                // wrong (member, not admin) the API returns 403; we
                // already gate on `canManage` so this should be rare.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [projectId, canManage, enabled, location.pathname, apiBase]);

    const dismiss = () => {
        try {
            window.localStorage.setItem(
                dismissalKey(projectId),
                "1"
            );
        } catch {
            /* private mode */
        }
        setOpen(false);
    };

    const connect = () => {
        dismiss();
        navigate(`${routeBase}/${projectId}/backups`);
    };

    if (!canManage) return null;

    return (
        <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : dismiss())}>
            <DialogContent
                data-testid="backup-safety-modal"
                className="max-w-md"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ShieldAlert size={16} className="text-amber-300" />
                        Backups protect your project
                    </DialogTitle>
                    <DialogDescription className="leading-relaxed">
                        Connect a backup provider so every continuity image,
                        script, schedule, and document is mirrored off-platform
                        as it&rsquo;s uploaded. You can always continue without
                        backup — but you&rsquo;ll see a small{" "}
                        <span className="font-semibold">No backup</span> badge
                        in the header until you connect.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={dismiss}
                        data-testid="backup-safety-continue"
                    >
                        Continue without backup
                    </Button>
                    <Button
                        onClick={connect}
                        data-testid="backup-safety-connect"
                    >
                        <Cloud size={13} className="mr-1.5" />
                        Connect Dropbox
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
